import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Edit2, ArrowLeft, Settings, Eye, EyeOff } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import APIRequest from '../api/APIRequest';
import { useSelector } from 'react-redux';

const StudentView = ({ studentData: propStudentData }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { error } = useToast();
  const [studentData, setStudentData] = useState(propStudentData);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const { user } = useSelector((state) => state.auth);
  const [showPassword, setShowPassword] = useState(false);

  // Fetch student data when component mounts (only if not passed as props)
  useEffect(() => {
    const fetchStudentData = async () => {
      if (!id || propStudentData) return;
      
      setLoading(true);
      try {
        const api = new APIRequest();
        const response = await api.request(`/admin/getStudent?id=${id}`, 'GET', {}, user.token);
        if (response.success) {
          setStudentData(response.student);
        } else {
          error(response.message || 'Failed to fetch student details');
        }
      } catch (err) {
        console.error('Error fetching student data:', err);
        error('Failed to fetch student details');
      } finally {
        setLoading(false);
      }
    };

    fetchStudentData();
  }, [id, user.token, error, propStudentData]);

  const handleEdit = () => {
    navigate(`/students/${id}/edit`, { state: { studentData } });
  };

  const handleBackToList = () => {
    navigate('/students');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!studentData) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No student data available</p>
      </div>
    );
  }

  const getClassDisplayText = () => {
    const studentClass = studentData?.class;
    if (studentClass && typeof studentClass === 'object') {
      return `${studentClass.name} - Section ${studentClass.section}`;
    }
    return studentClass || 'Not assigned';
  };

  const tabs = [
    { id: 'personal', label: 'Personal Information' },
    { id: 'academic', label: 'Academic Details' }
  ];

  return (
    <div className="min-h-screen bg-slate-50/50 pb-12">
      {/* Cover Header */}
      <div className="h-48 bg-gradient-to-r from-blue-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.1] bg-[size:20px_20px]" />
        <button
          onClick={handleBackToList}
          className="absolute top-6 left-6 p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all backdrop-blur-md flex items-center gap-2 group"
        >
          <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>

      {/* Profile Card & Content Container */}
      <div className="max-w-7xl mx-auto px-6 -mt-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Left Column: Profile Card */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 border border-slate-100 overflow-hidden">
              <div className="p-8 flex flex-col items-center text-center">
                <div className="relative mb-6">
                  {studentData.image ? (
                    <img 
                      src={`/${studentData.image}`} 
                      alt={studentData.name} 
                      className="w-32 h-32 rounded-3xl object-cover ring-4 ring-white shadow-lg shadow-blue-900/10"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div className={`w-32 h-32 bg-gradient-to-br from-blue-100 to-blue-50 rounded-3xl flex items-center justify-center ring-4 ring-white shadow-lg shadow-blue-900/10 ${studentData.image ? 'hidden' : ''}`}>
                    <span className="text-blue-600 font-black text-3xl">
                      {studentData.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-slate-900 mb-1">{studentData.name}</h2>
                <p className="text-blue-600 font-bold text-sm bg-blue-50 px-3 py-1 rounded-full mb-6">
                  {studentData.regNo || 'NO-REG-ID'}
                </p>

                <div className="w-full space-y-3 pt-6 border-t border-slate-100">
                  <div className="flex justify-between items-center text-sm px-2">
                    <span className="text-slate-500 font-medium">Class</span>
                    <span className="text-slate-900 font-bold">{getClassDisplayText()}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm px-2">
                    <span className="text-slate-500 font-medium">Gender</span>
                    <span className="text-slate-900 font-bold capitalize">{studentData.gender}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm px-2">
                    <span className="text-slate-500 font-medium">Fees</span>
                    <span className="text-green-600 font-black text-lg">{formatCurrency(studentData.fees)}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-slate-50/50 flex gap-3 border-t border-slate-100">
                <button 
                  onClick={handleEdit}
                  className="w-full bg-white hover:bg-slate-50 text-slate-700 font-bold py-3 rounded-2xl border border-slate-200 transition-all flex items-center justify-center gap-2 shadow-sm"
                >
                  <Edit2 size={16} />
                  Edit Profile
                </button>
              </div>
            </div>

            {/* Quick Contact Card */}
            <div className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 border border-slate-100 p-6 space-y-4">
              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest pl-1">Primary Contact</h4>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center text-orange-600">
                    <Settings size={20} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase block">Phone</label>
                    <p className="text-slate-900 font-bold">{studentData.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                    <Settings size={20} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-slate-400 uppercase block">Email</label>
                    <p className="text-slate-900 font-bold truncate max-w-[180px]">{studentData.email}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Tabbed Content */}
          <div className="lg:col-span-8 space-y-8">
            {/* Tabs Header */}
            <div className="bg-white p-2 rounded-2xl shadow-xl shadow-blue-900/5 border border-slate-100 flex gap-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-3 px-4 rounded-xl text-sm font-bold transition-all ${
                    activeTab === tab.id 
                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' 
                    : 'text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 border border-slate-100 p-8 min-h-[500px] animate-fadeIn">
              
              {activeTab === 'personal' && (
                <div className="space-y-8">
                  {/* Mobile Login Credentials (integrated at top) */}
                  <div className="bg-blue-50/50 p-6 rounded-3xl border border-blue-100 flex flex-wrap items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-blue-100 flex items-center justify-center text-blue-600">
                        <Settings size={24} />
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-blue-400 uppercase tracking-[0.2em] block mb-0.5">Mobile Login ID</label>
                        <p className="text-slate-900 font-black text-lg">{studentData.regNo || 'N/A'}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 md:border-l md:border-blue-100 md:pl-6">
                      <div>
                        <label className="text-[10px] font-black text-blue-400 uppercase tracking-[0.2em] block mb-0.5">Password</label>
                        <div className="flex items-center gap-3">
                          <p className="text-slate-900 font-mono font-black text-lg tracking-tight">
                            {showPassword ? (studentData.password || 'Not set') : '••••••••'}
                          </p>
                          {studentData.password && (
                            <button
                              onClick={() => setShowPassword(!showPassword)}
                              className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded-xl transition-all"
                              title={showPassword ? "Hide Password" : "Show Password"}
                            >
                              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-b border-slate-50 pb-4 pt-2">
                    <h3 className="text-xl font-bold text-slate-900">Personal Information</h3>
                    <p className="text-sm text-slate-500">Basic biological and contact details</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <InfoField label="Full Name" value={studentData.name} />
                    <InfoField label="Date of Birth" value={studentData.dateOfBirth} />
                    <InfoField label="Gender" value={studentData.gender} capitalize />
                    <InfoField label="Nationality" value={studentData.nationality} />
                    <div className="md:col-span-2">
                      <InfoField label="Home Address" value={studentData.address} />
                    </div>
                  </div>
                  
                  {studentData.contactPerson && studentData.contactPerson.length > 0 && (
                    <div className="pt-8 mt-8 border-t border-slate-100">
                      <h4 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6">Emergency Contacts</h4>
                      <div className="grid grid-cols-1 gap-4">
                        {studentData.contactPerson.map((contact, index) => (
                          <div key={index} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                            <div className="flex gap-4 items-center">
                              <div className="w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 font-bold">
                                {contact.relation?.[0]}
                              </div>
                              <div>
                                <p className="text-slate-900 font-bold">{contact.name}</p>
                                <p className="text-xs text-slate-500 font-bold uppercase">{contact.relation}</p>
                              </div>
                            </div>
                            <p className="text-blue-600 font-black">{contact.phone}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'academic' && (
                <div className="space-y-8">
                  <div className="border-b border-slate-50 pb-4">
                    <h3 className="text-xl font-bold text-slate-900">Academic Details</h3>
                    <p className="text-sm text-slate-500">School registration and placement information</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <InfoField label="Enrolled Class" value={getClassDisplayText()} highlight />
                    <InfoField label="Registration ID" value={studentData.regNo} highlight />
                    <InfoField label="Academic Year" value={studentData.academicYear} />
                    <InfoField label="Mother Tongue" value={studentData.motherLanguage} />
                    <InfoField label="Blood Group" value={studentData.bloodGroup} />
                    <InfoField label="Aadhar Number" value={studentData.aadharNumber} />
                    <InfoField label="Birth Certificate #" value={studentData.birthCertificateNumber} />
                  </div>
                </div>
              )}



            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Utility components and functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

// Internal component for info fields to keep JSX clean
const InfoField = ({ label, value, highlight, capitalize }) => (
  <div className="space-y-1">
    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block pl-1">{label}</label>
    <div className={`p-4 rounded-2xl border ${highlight ? 'bg-blue-50/30 border-blue-100' : 'bg-slate-50/30 border-slate-100'}`}>
      <p className={`text-slate-900 font-bold ${capitalize ? 'capitalize' : ''} ${highlight ? 'text-blue-700' : ''}`}>
        {value || 'N/A'}
      </p>
    </div>
  </div>
);

export default StudentView;
